import {createDialogOverlay, fillDialog, pvpButton} from "./DialogHelpers.js";
import {hostDialogAvailable, requestHostToolPrompt} from "./HostDialog.js";

import {t} from "./i18n/index.js";
// Shared "fixed distance" modal popup for the "Vinkelret / parallel" tool
// family (Vinkelret punkt, Vinkelret linje, Parallelforskydning) - shown once
// per tool activation, BEFORE the baseline/registration workflow starts (see
// each tool's customCapture()). One implementation for all three tools, per
// task requirement - do not duplicate this per tool.
//
// Same visual pattern as WallThicknessPrompt.js (dark #2b2b2b modal,
// Annuller/Fortsæt), reused here rather than re-invented, but this is its own
// file since the two prompts ask semantically different questions and are
// triggered at different points in their respective tools' lifecycles.
//
// Resolves to:
//   a number  - the fixed distance the user entered; the tool should lock
//               every placed point/offset to this distance
//   null      - the user explicitly continued with an empty field, meaning
//               "keep the existing dynamic, cursor-driven distance"
//   undefined - the user cancelled (Annuller / ESC / tool switched away
//               while the popup was still open) - the caller must NOT start
//               the tool's capture workflow at all

// Pure parsing, exported for Level 1 (no-DOM) unit testing - see
// DistancePrompt.test.js. Mirrors WallThicknessPrompt.js's own submit()
// convention: an empty or non-numeric field is "no fixed value", not an
// error to reject - the popup has no invalid state, only "set" or "not set".
export function parseDistanceInput(raw){
	if(raw === undefined || raw === null) return null;
	const trimmed = String(raw).trim();
	if(trimmed === '') return null;

	const num = parseFloat(trimmed);
	return isFinite(num) ? num : null;
}

// options: {title, description, defaultValue?} - defaultValue (a number, or
// null/undefined for none) prefills the input but the popup is still shown
// either way (task requirement: a configured default only prefills the
// field, it never skips asking).
export function promptDistance(dm, {title, description, defaultValue = null} = {}){
	// Vært-UI (PointViews frontend) viser dialogen i PointViews fælles overlay-design; Potree ejer stadig værktøjets
	// logik og tolkningen af svaret (parseDistanceInput). Uden vært (standalone Potree): egen DOM-dialog herunder.
	if(hostDialogAvailable(dm.viewer)){
		return promptDistanceViaHost(dm, {title, description, defaultValue});
	}
	return new Promise((resolve) => {
		const {overlay, box} = createDialogOverlay(dm, 280);
		const prefill = (typeof defaultValue === 'number' && isFinite(defaultValue) && defaultValue !== 0) ? defaultValue : '';
		fillDialog(box, {
			title: `${title || t('Afstand')}`,
			description: `${description || t('Angiv en fast afstand, eller lad feltet stå tomt for at vælge afstanden dynamisk med musen under registrering.')}`,
			body: `<label class="pvp-label">${t("Afstand [m]")}</label>
			<input class="pvp-input" type="number" step="any" placeholder="${t("Valgfri - tom = dynamisk")}" value="${prefill}"
			 />`,
			actions: pvpButton('cancel', t("Annuller")) + pvpButton('continue', t("Fortsæt"), 'primary'),
		});

		const input = box.querySelector('input');
		input.focus();
		input.select();

		let done = false;
		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};
		const finish = (value) => {
			if(done) return;
			done = true;
			cleanup();
			resolve(value);
		};

		const submit = () => finish(parseDistanceInput(input.value));
		const onCancelEvent = () => finish(undefined);

		box.querySelector('[data-action="continue"]').addEventListener('click', submit);
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(undefined));
		box.addEventListener('keydown', (e) => {
			// stopPropagation: the popup's own input is focused, which makes
			// DigitizationModule's global Escape handler skip itself (it treats
			// a focused text field as "editing a value, not cancelling the
			// tool") - so Escape must be handled here directly instead.
			if(e.key === 'Escape'){ e.stopPropagation(); finish(undefined); }
			if(e.key === 'Enter'){ e.preventDefault(); submit(); }
		});

		// covers ESC pressed outside the popup, or the tool being switched
		// away from some other way while the popup is still open
		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}

// Samme kontrakt som den indbyggede dialog (number | null | undefined), men dialogen renderes af værten.
// "cancel_insertions" (Esc udenfor dialogen, værktøjet skiftet væk) afbryder dialogen via AbortSignal.
function promptDistanceViaHost(dm, {title, description, defaultValue}){
	const prefill = (typeof defaultValue === 'number' && isFinite(defaultValue) && defaultValue !== 0) ? defaultValue : '';
	const spec = {
		kind: 'form',
		size: 's',
		title: title || t('Afstand'),
		text: description || t('Angiv en fast afstand, eller lad feltet stå tomt for at vælge afstanden dynamisk med musen under registrering.'),
		fields: [{id: 'distance', type: 'number', label: t('Afstand [m]'), placeholder: t('Valgfri - tom = dynamisk'), value: String(prefill), step: 'any'}],
		confirmLabel: t('Fortsæt'),
		cancelLabel: t('Annuller'),
	};

	// Esc afbryder værktøjet, "cancel_insertions" afbryder dialogen (requestHostToolPrompt)
	return requestHostToolPrompt(dm.viewer, spec).then((result) => {
		return result.action === 'confirm' ? parseDistanceInput(result.values.distance) : undefined;
	});
}
